import { useId } from "react";

import { useBranding } from "@/api/hooks/tenant";
import { productName } from "@/lib/branding";
import { cn } from "@/lib/utils";

interface LogoProps {
  className?: string;
  /** Show the wordmark (the product's name with a period, or the tenant's name) next to the mark. */
  wordmark?: boolean;
  /** "sm" for app chrome (24px tile), "md" for public pages (26px tile). */
  size?: "sm" | "md";
}

/**
 * The site's logo: the product's mark and name; on a tenant's host its logo (the tenant's name as its alt text),
 * or without one the product's mark and the tenant's name (white label: never "Hume").
 */
export function Logo(props: LogoProps) {
  const branding = useBranding();
  if (branding.is_root) return <ProductLogo name={productName(branding)} {...props} />;
  if (branding.logo_url) return <TenantLogo src={branding.logo_url} name={branding.name} {...props} />;
  return <ProductLogo name={branding.name} tenant {...props} />;
}

function TenantLogo({ src, name, className, size = "md" }: LogoProps & { src: string; name: string }) {
  return (
    <span className={cn("inline-flex min-w-0 items-center text-foreground", className)}>
      <img
        src={src}
        alt={name}
        className={cn("w-auto max-w-40 shrink-0 object-contain", size === "sm" ? "h-6" : "h-[26px]")}
      />
    </span>
  );
}

/** The product's mark and, as the wordmark, its name with a period (or a logo-less tenant's name, as it is). */
function ProductLogo({
  name,
  tenant = false,
  className,
  wordmark = true,
  size = "md",
}: LogoProps & { name: string; tenant?: boolean }) {
  return (
    <span className={cn("group/logo inline-flex min-w-0 items-center gap-2.5 text-foreground", className)}>
      <ProductMark size={size} />
      {wordmark && (
        <span
          className={cn(
            "font-bold tracking-[-0.02em]",
            tenant && "truncate",
            size === "sm" ? "text-base" : "text-[17px]",
          )}
        >
          {tenant ? name : `${name}.`}
        </span>
      )}
    </span>
  );
}

/**
 * The product's mark: Learn's rounded triangle (Hire's is a circle) in Learn's pink gradient, on a dark rounded tile.
 * The gradient runs across the triangle's box (user space), as in the favicon and app icons (public/). Also the logo
 * of a tenant without one of its own.
 */
function ProductMark({ size = "md" }: Pick<LogoProps, "size">) {
  // Unique per instance: a shared id would resolve to the first (possibly hidden) logo's gradient.
  const gradientId = useId();
  return (
    <svg
      viewBox="0 0 50 50"
      data-slot="product-mark"
      className={cn(
        "shrink-0 group-hover/logo:rotate-180 motion-safe:transition-transform motion-safe:duration-200 motion-safe:ease-in-out",
        size === "sm" ? "size-6" : "size-[26px]",
      )}
      aria-hidden
    >
      <defs>
        <linearGradient id={gradientId} gradientUnits="userSpaceOnUse" x1="4" y1="11" x2="46" y2="39">
          <stop offset="0" stopColor="#03001e" />
          <stop offset="0.29" stopColor="#7303c0" />
          <stop offset="0.67" stopColor="#ec38bc" />
          <stop offset="1" stopColor="#fdeff9" />
        </linearGradient>
      </defs>
      <rect width="50" height="50" rx="5" fill="#252525" />
      <path
        d="M25 11 40.6 38H9.4Z"
        fill={`url(#${gradientId})`}
        stroke={`url(#${gradientId})`}
        strokeWidth="5"
        strokeLinejoin="round"
      />
    </svg>
  );
}
